/**
 * useFavouriteLists — React Query hook for the engineer's favourite lists
 * (DEV-51010). A list is a named group of favourites, e.g. "Tue EV job".
 *
 * Provides:
 *   - `lists` — every list, most recently changed first. Each carries the
 *     ids of the favourites in it (newest first); screens join those
 *     against `useFavourites().favourites` for rows and thumbnails.
 *   - `setInList` — tick / untick a favourite in a list. Applied to the
 *     cache at once so the sheet's count moves on tap, then reconciled
 *     with the server's copy of that list.
 *   - `createList` / `renameList` / `deleteList` — dialogs await these so
 *     they can stay open and show the server's message on failure.
 *
 * Un-heart and Undo live in useFavourites, which updates this cache too.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'

import { api } from '../../services/api'
import { sortListsByRecent } from '../../utils/favouriteLists'
import type { FavouriteList } from '../../types'
import { HOME_TAB_ENABLED } from '../../config/features'

const MEMBERSHIP_MUTATION_KEY = ['favourite-list-membership'] as const

export function favouriteListsQueryKey(scope: string) {
  return ['favourite-lists', scope] as const
}

interface MembershipChange {
  listId: string
  favouriteId: string
  included: boolean
}

function replaceList(lists: FavouriteList[] | undefined, next: FavouriteList): FavouriteList[] {
  return (lists ?? []).map((list) => (list.id === next.id ? next : list))
}

export function useFavouriteLists() {
  const queryClient = useQueryClient()
  const { tenantSlug } = useParams<{ tenantSlug: string }>()
  const listsKey = favouriteListsQueryKey(tenantSlug || 'default')

  // Same build-flag guard as useFavourites: lists only exist alongside the
  // Favourites page, so environments without it never call the endpoint.
  const listsQuery = useQuery<FavouriteList[]>({
    queryKey: listsKey,
    queryFn: () => api.listFavouriteLists(),
    staleTime: 60 * 1000,
    enabled: HOME_TAB_ENABLED,
  })

  const membershipMutation = useMutation({
    mutationKey: MEMBERSHIP_MUTATION_KEY,
    mutationFn: ({ listId, favouriteId, included }: MembershipChange) =>
      included
        ? api.addToFavouriteList(listId, favouriteId)
        : api.removeFromFavouriteList(listId, favouriteId),
    onMutate: async ({ listId, favouriteId, included }: MembershipChange) => {
      await queryClient.cancelQueries({ queryKey: listsKey })
      const previous = queryClient.getQueryData<FavouriteList[]>(listsKey)
      const now = new Date().toISOString()
      queryClient.setQueryData<FavouriteList[]>(listsKey, (lists) =>
        (lists ?? []).map((list) => {
          if (list.id !== listId) return list
          const without = list.favouriteIds.filter((id) => id !== favouriteId)
          return {
            ...list,
            favouriteIds: included ? [favouriteId, ...without] : without,
            updatedAt: now,
          }
        }),
      )
      return { previous }
    },
    onError: (_error, _change, context) => {
      if (context?.previous) queryClient.setQueryData(listsKey, context.previous)
    },
    onSuccess: (list) => {
      queryClient.setQueryData<FavouriteList[]>(listsKey, (lists) => replaceList(lists, list))
    },
    onSettled: () => {
      // Refetch once the last of a burst of ticks lands, so out-of-order
      // responses can't leave a stale count behind.
      if (queryClient.isMutating({ mutationKey: MEMBERSHIP_MUTATION_KEY }) === 1) {
        queryClient.invalidateQueries({ queryKey: listsKey })
      }
    },
  })

  const createMutation = useMutation({
    mutationFn: ({ name, favouriteId }: { name: string; favouriteId?: string }) =>
      api.createFavouriteList(name, favouriteId),
    onSuccess: (list) => {
      queryClient.setQueryData<FavouriteList[]>(listsKey, (lists) => [list, ...(lists ?? [])])
    },
  })

  const renameMutation = useMutation({
    mutationFn: ({ listId, name }: { listId: string; name: string }) =>
      api.renameFavouriteList(listId, name),
    onSuccess: (list) => {
      queryClient.setQueryData<FavouriteList[]>(listsKey, (lists) => replaceList(lists, list))
    },
  })

  // Optimistic: the list screen navigates away as soon as Delete is
  // confirmed, so the Lists tab must already be without it.
  const deleteMutation = useMutation({
    mutationFn: (listId: string) => api.deleteFavouriteList(listId),
    onMutate: async (listId: string) => {
      await queryClient.cancelQueries({ queryKey: listsKey })
      const previous = queryClient.getQueryData<FavouriteList[]>(listsKey)
      queryClient.setQueryData<FavouriteList[]>(listsKey, (lists) =>
        (lists ?? []).filter((list) => list.id !== listId),
      )
      return { previous }
    },
    onError: (_error, _listId, context) => {
      if (context?.previous) queryClient.setQueryData(listsKey, context.previous)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: listsKey })
    },
  })

  return {
    lists: sortListsByRecent(listsQuery.data ?? []),
    isLoading: listsQuery.isLoading,
    isError: listsQuery.isError,
    refetch: listsQuery.refetch,

    // All async: callers catch to show their own toast or inline error.
    // (Per-call `mutate` callbacks only fire for the latest call and not
    // after unmount — a burst of ticks, or Delete then navigating away,
    // would drop them.)
    setInList: (change: MembershipChange) => membershipMutation.mutateAsync(change),
    createList: (name: string, favouriteId?: string) =>
      createMutation.mutateAsync({ name, favouriteId }),
    renameList: (listId: string, name: string) => renameMutation.mutateAsync({ listId, name }),
    deleteList: (listId: string) => deleteMutation.mutateAsync(listId),
  }
}
